import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Odemeler } from "../components/Odemeler";
import { NakitMatris } from "../components/NakitMatris";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  FileText,
  Banknote,
  HandCoins,
  AlertTriangle,
  Clock,
  Loader2,
  Filter,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Settings2,
  ArrowUpCircle,
  ArrowDownCircle,
  FileSpreadsheet,
  CalendarRange,
  Table2,
  Wallet,
  Landmark,
  PencilLine,
  UserRound,
} from "lucide-react";

const AYLAR_KISA = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const AYLAR_TAM = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const GUN_ADLARI = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const STANDART_KANALLAR = ["Sigorta-Anlaşmalı", "Sigorta-Anlaşmasız", "Bireysel", "Kurumsal", "Belirtilmemiş"];

const KOVALAR = [
  { key: "0_30", etiket: "0–30 gün", aciklama: "Yeni alacaklar", renk: "border-amber-200 bg-amber-50/60", metin: "text-amber-800", vurgu: false },
  { key: "31_60", etiket: "31–60 gün", aciklama: "Takip edilmeli", renk: "border-orange-300 bg-orange-50/70", metin: "text-orange-800", vurgu: false },
  { key: "60_plus", etiket: "60+ gün", aciklama: "Gecikmiş — acil aksiyon", renk: "border-red-400 bg-red-50", metin: "text-red-700", vurgu: true },
];

export default function NakitAkisPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kanal, setKanal] = useState("tumu");
  const [kovaDialog, setKovaDialog] = useState(null); // kova key
  const [tab, setTab] = useState("matris");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/nakit-akis", { params: { yil: 2026, kanal } });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Nakit akış verileri yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [kanal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="space-y-4" data-testid="nakit-akis-loading">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!data) return null;

  const kanalSecenekleri = Array.from(new Set([...STANDART_KANALLAR, ...(data.kanallar || [])]));

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/15 hover:bg-[hsl(var(--primary))]/5" data-testid="nakit-akis-yil-badge">
          {data.yil} • Rapor tarihi: {formatDate(data.bugun)}
        </Badge>
        <span className="text-xs text-muted-foreground">Tutarlar matrah (KDV hariç) bazlıdır; açık alacak KDV dahildir.</span>
      </div>

      <VadesizUyari />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-11 bg-muted/60 p-1 flex-wrap">
          <TabsTrigger value="matris" className="h-9 gap-1.5 px-4" data-testid="nakit-tab-matris">
            <Table2 className="h-4 w-4" />
            Nakit Matrisi
          </TabsTrigger>
          <TabsTrigger value="gunluk" className="h-9 gap-1.5 px-4" data-testid="nakit-tab-gunluk">
            <CalendarDays className="h-4 w-4" />
            Günlük Akış
          </TabsTrigger>
          <TabsTrigger value="haftalik" className="h-9 gap-1.5 px-4" data-testid="nakit-tab-haftalik">
            <CalendarRange className="h-4 w-4" />
            Haftalık Akış
          </TabsTrigger>
          <TabsTrigger value="aylik-akis" className="h-9 gap-1.5 px-4" data-testid="nakit-tab-aylik-akis">
            <Table2 className="h-4 w-4" />
            Aylık Akış
          </TabsTrigger>
          <TabsTrigger value="aylik" className="h-9 gap-1.5 px-4" data-testid="nakit-tab-aylik">
            <FileText className="h-4 w-4" />
            Yıllık Özet
          </TabsTrigger>
          <TabsTrigger value="vade" className="h-9 gap-1.5 px-4" data-testid="nakit-tab-vade">
            <Clock className="h-4 w-4" />
            Vade Takvimi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="matris" className="mt-4">
          <NakitMatris />
        </TabsContent>

        <TabsContent value="gunluk" className="mt-4 space-y-4">
          <GunlukAkis />
        </TabsContent>

        <TabsContent value="haftalik" className="mt-4">
          <HaftalikAkis />
        </TabsContent>

        <TabsContent value="aylik-akis" className="mt-4">
          <AylikAkis />
        </TabsContent>

        <TabsContent value="aylik" className="mt-4 space-y-5">
      {/* BLOK A — TAHAKKUK BAZLI */}
      <AkisTablosu
        testid="nakit-blok-a"
        baslik="Blok A — Tahakkuk Bazlı Nakit Akış"
        altBaslik="Fatura tarihine göre • Faturalanan Ciro − Faaliyet Gideri − SMM = Net Akış"
        satirlar={[
          { etiket: "Faturalanan Ciro", degerler: data.tahakkuk.ciro, tip: "duz" },
          { etiket: "Faaliyet Gideri (−)", degerler: data.tahakkuk.faaliyet_gideri, tip: "eksi" },
          { etiket: "SMM (−)", degerler: data.tahakkuk.smm, tip: "eksi" },
          { etiket: "NET AKIŞ", degerler: data.tahakkuk.net, tip: "net" },
          { etiket: "KÜMÜLATİF", degerler: data.tahakkuk.kumulatif, tip: "kumulatif" },
        ]}
      />

      {/* BLOK B — NAKİT BAZLI */}
      <AkisTablosu
        testid="nakit-blok-b"
        baslik="Blok B — Nakit Bazlı Nakit Akış"
        altBaslik="Tahsilat tarihine göre • Tahsil Edilen − Faaliyet Gideri − SMM = Nakit Akış"
        uyari={
          data.nakit.tahsilat.every((v) => v === 0)
            ? "Henüz tahsilat kaydı bulunmuyor. Ciro ekranından kayıtları “Tahsil Edildi” olarak işaretledikçe bu tablo dolacaktır."
            : null
        }
        satirlar={[
          { etiket: "Tahsil Edilen", degerler: data.nakit.tahsilat, tip: "duz" },
          { etiket: "Faaliyet Gideri (−)", degerler: data.nakit.faaliyet_gideri, tip: "eksi" },
          { etiket: "SMM (−)", degerler: data.nakit.smm, tip: "eksi" },
          { etiket: "NAKİT AKIŞ", degerler: data.nakit.net, tip: "net" },
          { etiket: "KÜMÜLATİF", degerler: data.nakit.kumulatif, tip: "kumulatif" },
        ]}
      />

      {/* BLOK C — AÇIK ALACAK & YAŞLANDIRMA */}
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="nakit-blok-c">
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <div>
            <h3 className="font-display text-sm font-semibold">Blok C — Açık Alacak &amp; Yaşlandırma</h3>
            <p className="text-[11px] text-muted-foreground">Bugüne göre canlı • KDV dahil tutarlar • fatura tarihine göre yaş</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Filter className="h-3.5 w-3.5" />
              Kanal
            </Label>
            <Select value={kanal} onValueChange={setKanal}>
              <SelectTrigger className="h-10 w-48" data-testid="nakit-kanal-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="tumu">Tüm Kanallar</SelectItem>
                {kanalSecenekleri.map((k) => (
                  <SelectItem key={k} value={k}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </div>

        <div className="space-y-4 p-4">
          {/* Özet KPI */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="nakit-alacak-ozet">
            <OzetKart
              icon={FileText}
              renk="#2563EB"
              baslik="Toplam Faturalanan"
              deger={formatTL(data.alacak.toplam_faturalanan)}
              alt={`${data.alacak.fatura_adet.toLocaleString("tr-TR")} kayıt`}
              testid="nakit-toplam-faturalanan"
            />
            <OzetKart
              icon={HandCoins}
              renk="#16A34A"
              baslik="Toplam Tahsil Edilen"
              deger={formatTL(data.alacak.toplam_tahsil)}
              alt={`${data.alacak.tahsil_adet.toLocaleString("tr-TR")} kayıt`}
              testid="nakit-toplam-tahsil"
            />
            <OzetKart
              icon={Banknote}
              renk="#DC2626"
              baslik="AÇIK ALACAK"
              deger={formatTL(data.alacak.acik_alacak)}
              alt="Faturalanan − Tahsil Edilen"
              vurgulu
              testid="nakit-acik-alacak"
            />
          </div>

          {/* Yaş kovaları */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Alacak Yaşlandırma <span className="font-normal normal-case">(kutucuklara tıklayarak kayıtları görüntüleyin)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="nakit-kovalar">
              {KOVALAR.map((k) => {
                const v = data.alacak.kovalar[k.key];
                return (
                  <button
                    key={k.key}
                    onClick={() => setKovaDialog(k.key)}
                    className={`rounded-xl border p-4 text-left transition-transform duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] ${k.renk} ${k.vurgu ? "ring-1 ring-red-300" : ""}`}
                    data-testid={`nakit-kova-${k.key}`}
                  >
                    <div className={`flex items-center gap-1.5 ${k.metin}`}>
                      {k.vurgu ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                      <span className="text-xs font-bold">{k.etiket}</span>
                    </div>
                    <p className={`mt-2 font-display text-xl font-bold tabular-nums ${k.vurgu ? "text-red-700" : "text-foreground"}`}>
                      {formatTL(v.tutar)}
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {v.adet.toLocaleString("tr-TR")} kayıt • {k.aciklama}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Kova drill-down dialogu */}
      <KovaDialog kova={kovaDialog} kanal={kanal} onClose={() => setKovaDialog(null)} />
        </TabsContent>

        <TabsContent value="vade" className="mt-4">
          <VadeTakvimi />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}

/* ---------- 12 ay kolonlu akış tablosu ---------- */
function AkisTablosu({ baslik, altBaslik, satirlar, uyari, testid, onAyClick }) {
  const toplamlar = satirlar.map((s) =>
    s.tip === "kumulatif" ? s.degerler[11] : s.degerler.reduce((a, b) => a + b, 0)
  );

  const hucreSinifi = (tip, deger) => {
    if (tip === "net") return deger >= 0 ? "font-semibold text-emerald-700" : "font-semibold text-red-600";
    if (tip === "kumulatif") return deger >= 0 ? "font-bold text-white" : "font-bold text-red-300";
    if (tip === "eksi") return "text-muted-foreground";
    return "";
  };

  const formatDeger = (v) => {
    if (v === 0) return "—";
    if (v < 0) return `(${formatTL(Math.abs(v))})`;
    return formatTL(v);
  };

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid={testid}>
      <div className="border-b px-4 py-3">
        <h3 className="font-display text-sm font-semibold">{baslik}</h3>
        <p className="text-[11px] text-muted-foreground">{altBaslik}</p>
      </div>
      {uyari && (
        <div className="mx-4 mt-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-800" data-testid={`${testid}-uyari`}>
          {uyari}
        </div>
      )}
      <div className="w-full overflow-x-auto p-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="sticky left-0 z-10 bg-card px-3 py-2 text-left font-semibold min-w-[150px] border-r">Kalem</th>
              {AYLAR_KISA.map((a, i) => (
                <th key={a} className="px-2 py-2 text-right font-semibold min-w-[84px]">
                  {onAyClick ? (
                    <button
                      onClick={() => onAyClick(i)}
                      className="rounded px-1 py-0.5 uppercase underline decoration-dotted underline-offset-2 hover:bg-muted hover:text-foreground"
                      title={`${AYLAR_TAM[i]} detayını aç`}
                      data-testid={`${testid}-ay-btn-${i + 1}`}
                    >
                      {a}
                    </button>
                  ) : (
                    a
                  )}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-bold min-w-[100px] border-l-2 border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 text-[hsl(var(--primary))]">
                Toplam
              </th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((s, si) => {
              const kumulatifMi = s.tip === "kumulatif";
              const netMi = s.tip === "net";
              return (
                <tr
                  key={s.etiket}
                  className={
                    kumulatifMi
                      ? "bg-[hsl(var(--primary))] text-white"
                      : netMi
                        ? "border-t-2 border-[hsl(var(--primary))]/20 bg-muted/40"
                        : "border-t border-border/50"
                  }
                  data-testid={`${testid}-satir-${si}`}
                >
                  <td className={`sticky left-0 z-10 px-3 py-2 font-medium whitespace-nowrap border-r ${kumulatifMi ? "bg-[hsl(var(--primary))] border-white/10" : netMi ? "bg-muted/40 font-bold" : "bg-card"}`}>
                    {s.etiket}
                  </td>
                  {s.degerler.map((v, i) => (
                    <td key={i} className={`px-2 py-2 text-right tabular-nums whitespace-nowrap ${hucreSinifi(s.tip, v)}`}>
                      {formatDeger(v)}
                    </td>
                  ))}
                  <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap font-bold border-l-2 border-[hsl(var(--accent))]/40 ${kumulatifMi ? "bg-[hsl(var(--primary))] text-[hsl(var(--accent))]" : "bg-[hsl(var(--accent))]/5"} ${!kumulatifMi ? hucreSinifi(s.tip, toplamlar[si]) : ""}`}>
                    {formatDeger(toplamlar[si])}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Özet KPI kartı ---------- */
const OzetKart = ({ icon: Icon, renk, baslik, deger, alt, vurgulu = false, testid }) => (
  <div
    className={`flex items-center gap-3 rounded-xl border p-4 ${vurgulu ? "border-red-300 bg-red-50/60" : "bg-card"}`}
    data-testid={testid}
  >
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${renk}1A` }}>
      <Icon className="h-5 w-5" style={{ color: renk }} />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{baslik}</p>
      <p className={`font-display text-lg font-bold tabular-nums whitespace-nowrap ${vurgulu ? "text-red-700" : ""}`}>{deger}</p>
      <p className="text-[11px] text-muted-foreground truncate">{alt}</p>
    </div>
  </div>
);

/* ---------- Kova drill-down dialogu ---------- */
const KOVA_ETIKET = { "0_30": "0–30 gün", "31_60": "31–60 gün", "60_plus": "60+ gün" };

function KovaDialog({ kova, kanal, onClose }) {
  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!kova) {
      setVeri(null);
      return;
    }
    let iptal = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/nakit-akis/acik-kayitlar", { params: { kova, kanal } });
        if (!iptal) setVeri(res.data);
      } catch (err) {
        toast.error(getErrMsg(err, "Kayıtlar yüklenemedi."));
      } finally {
        if (!iptal) setLoading(false);
      }
    })();
    return () => {
      iptal = true;
    };
  }, [kova, kanal]);

  return (
    <Dialog open={!!kova} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="nakit-kova-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {kova === "60_plus" && <AlertTriangle className="h-5 w-5 text-red-600" />}
            Açık Alacaklar — {KOVA_ETIKET[kova] || ""}
          </DialogTitle>
          <DialogDescription>
            {veri ? `${veri.adet.toLocaleString("tr-TR")} açık kayıt` : "Yükleniyor…"}
            {kanal !== "tumu" ? ` • Kanal: ${kanal}` : " • Tüm kanallar"} • fatura tarihine göre gün
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto rounded-lg border">
          {loading ? (
            <div className="space-y-2 p-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : !veri || veri.kayitlar.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="nakit-kova-bos">
              Bu kovada açık kayıt bulunmuyor.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Müşteri</th>
                  <th className="px-3 py-2">Plaka/Dosya</th>
                  <th className="px-3 py-2">Fatura Tarihi</th>
                  <th className="px-3 py-2 text-right">Gün</th>
                  <th className="px-3 py-2 text-right">Tutar (KDV dahil)</th>
                  <th className="px-3 py-2">Durum</th>
                </tr>
              </thead>
              <tbody>
                {veri.kayitlar.map((r) => (
                  <tr key={r.id} className="border-t border-border/50" data-testid="nakit-kova-kayit">
                    <td className="px-3 py-2 max-w-[200px] truncate">{r.musteri_aciklama || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.plaka_dosya_no || "—"}</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatDate(r.tarih)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.gun > 60 ? "text-red-600" : ""}`}>
                      {r.gun}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap">{formatTL(r.toplam)}</td>
                    <td className="px-3 py-2">
                      <Badge
                        className={
                          r.tahsilat_durumu === "Kısmi"
                            ? "bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-50"
                            : "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50"
                        }
                      >
                        {r.tahsilat_durumu}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Vade Takvimi (Gün Gün) ---------- */
const DURUM_BADGE = {
  "Gerçekleşti": "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50",
  "Kısmi": "bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-50",
  "Bekliyor": "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50",
};

function VadeTakvimi() {
  const simdi = new Date();
  const [yil, setYil] = useState(String(simdi.getFullYear()));
  const [ay, setAy] = useState(String(simdi.getMonth() + 1));
  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acikGun, setAcikGun] = useState(null); // tarih string
  const [gecikenAcik, setGecikenAcik] = useState(false);
  const [sorumluFiltre, setSorumluFiltre] = useState("tumu");
  const [takipKayit, setTakipKayit] = useState(null);

  const fetchVeri = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/nakit-akis/vade-takvimi", { params: { yil: parseInt(yil), ay: parseInt(ay) } });
      setVeri(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Vade takvimi yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, ay]);

  useEffect(() => {
    fetchVeri();
  }, [fetchVeri]);

  const sorumlular = useMemo(() => {
    const s = new Set((veri?.geciken?.kayitlar || []).map((k) => k.takip_sorumlu).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b, "tr"));
  }, [veri]);

  const gecikenListe = useMemo(() => {
    const ks = veri?.geciken?.kayitlar || [];
    if (sorumluFiltre === "tumu") return ks;
    if (sorumluFiltre === "__bos") return ks.filter((k) => !k.takip_sorumlu);
    return ks.filter((k) => k.takip_sorumlu === sorumluFiltre);
  }, [veri, sorumluFiltre]);

  if (loading && !veri) {
    return (
      <div className="space-y-3" data-testid="vade-takvimi-loading">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!veri) return null;

  const gunAdi = (t) => {
    try { return GUN_ADLARI[new Date(t + "T12:00:00").getDay()]; } catch { return ""; }
  };

  return (
    <div className="space-y-4" data-testid="vade-takvimi">
      {/* Ay / Yıl seçimi */}
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <CalendarDays className="h-3.5 w-3.5" /> Dönem
        </Label>
        <Select value={ay} onValueChange={setAy}>
          <SelectTrigger className="h-10 w-36" data-testid="vade-ay-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {AYLAR_TAM.map((a, i) => (
              <SelectItem key={a} value={String(i + 1)}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={yil} onValueChange={setYil}>
          <SelectTrigger className="h-10 w-28" data-testid="vade-yil-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {["2025", "2026", "2027"].map((y) => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <span className="text-[11px] text-muted-foreground">
          Vade tarihi = tahsilatın beklendiği tarih • tutarlar KDV dahil
        </span>
      </div>

      {/* Özet kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="vade-ozet-kartlari">
        <div className="rounded-xl border bg-card p-4" data-testid="vade-ozet-beklenen">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{AYLAR_TAM[parseInt(ay) - 1]} Beklenen</p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums">{formatTL(veri.ozet.beklenen)}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{veri.ozet.adet.toLocaleString("tr-TR")} kayıt vadeli</p>
        </div>
        <div className="rounded-xl border bg-card p-4" data-testid="vade-ozet-gerceklesen">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Gerçekleşen</p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-emerald-700">{formatTL(veri.ozet.gerceklesen)}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Tahsil edilen kısım</p>
        </div>
        <div className="rounded-xl border bg-card p-4" data-testid="vade-ozet-kalan">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Kalan Beklenen</p>
          <p className={`mt-1 font-display text-xl font-bold tabular-nums ${veri.ozet.kalan > 0 ? "text-amber-700" : "text-emerald-700"}`}>{formatTL(veri.ozet.kalan)}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Beklenen − Gerçekleşen</p>
        </div>
        <div className="rounded-xl border border-red-300 bg-red-50/60 p-4" data-testid="vade-ozet-geciken">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-red-700 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Geciken (Tüm Zamanlar)
          </p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-red-700">{formatTL(veri.geciken.toplam)}</p>
          <p className="mt-0.5 text-[11px] text-red-700/70">{veri.geciken.adet.toLocaleString("tr-TR")} kayıt vadesi geçti</p>
        </div>
      </div>

      {/* Gün gün liste */}
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="vade-gun-listesi">
        <div className="border-b bg-muted/40 px-4 py-3">
          <p className="text-sm font-semibold">{AYLAR_TAM[parseInt(ay) - 1]} {yil} — Gün Gün Beklenen Tahsilatlar</p>
          <p className="text-xs text-muted-foreground">Satıra tıklayınca kaynak faturalar açılır • Beklenen / Gerçekleşen / Kalan</p>
        </div>
        {veri.gunler.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="vade-gun-bos">
            Bu ayda vadesi gelen kayıt bulunmuyor.
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {veri.gunler.map((g) => {
              const acik = acikGun === g.tarih;
              const bugunMu = g.tarih === veri.bugun;
              return (
                <div key={g.tarih}>
                  <button
                    onClick={() => setAcikGun(acik ? null : g.tarih)}
                    className={`flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] ${g.gecikti && g.kalan > 0 ? "bg-red-50/40" : ""} ${bugunMu ? "bg-[hsl(var(--accent))]/5" : ""}`}
                    data-testid={`vade-gun-satiri-${g.tarih}`}
                  >
                    {acik ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <div className="min-w-[130px]">
                      <p className="text-sm font-semibold tabular-nums">
                        {formatDate(g.tarih)}
                        {bugunMu && <Badge className="ml-1.5 bg-[hsl(var(--accent))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--accent))]/30 text-[9px] hover:bg-[hsl(var(--accent))]/15">BUGÜN</Badge>}
                      </p>
                      <p className="text-[11px] text-muted-foreground">{gunAdi(g.tarih)} • {g.adet} kayıt</p>
                    </div>
                    <div className="ml-auto grid grid-cols-3 gap-4 text-right">
                      <div>
                        <p className="text-[10px] uppercase text-muted-foreground">Beklenen</p>
                        <p className="text-sm font-bold tabular-nums">{formatTL(g.beklenen)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-muted-foreground">Gerçekleşen</p>
                        <p className="text-sm font-bold tabular-nums text-emerald-700">{formatTL(g.gerceklesen)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-muted-foreground">Kalan</p>
                        <p className={`text-sm font-bold tabular-nums ${g.kalan > 0 ? (g.gecikti ? "text-red-700" : "text-amber-700") : "text-emerald-700"}`}>
                          {g.kalan > 0 ? formatTL(g.kalan) : "✓"}
                        </p>
                      </div>
                    </div>
                  </button>
                  {acik && (
                    <div className="border-t border-border/60 bg-muted/20 px-4 py-2 overflow-x-auto" data-testid={`vade-gun-detay-${g.tarih}`}>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                            <th className="px-2 py-1.5">Belge / İş Emri</th>
                            <th className="px-2 py-1.5">Sigorta / Müşteri</th>
                            <th className="px-2 py-1.5">Kanal</th>
                            <th className="px-2 py-1.5 text-right">Tutar</th>
                            <th className="px-2 py-1.5 text-right">Tahsil Edilen</th>
                            <th className="px-2 py-1.5 text-right">Kalan</th>
                            <th className="px-2 py-1.5">Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.kayitlar.map((k) => (
                            <tr key={k.id} className="border-t border-border/40" data-testid="vade-kayit-satiri">
                              <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                                {k.belge_no || k.is_emri_no || "—"}
                                {k.kayit_tipi === "İş Emri" && <Badge className="ml-1 border-sky-200 bg-sky-50 text-sky-700 text-[9px] border hover:bg-sky-50">İş Emri</Badge>}
                              </td>
                              <td className="px-2 py-1.5 max-w-[220px] truncate" title={k.sigorta_sirketi || k.musteri}>{k.sigorta_sirketi || k.musteri || "—"}</td>
                              <td className="px-2 py-1.5 whitespace-nowrap">{k.kanal}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.toplam)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap text-emerald-700">{formatTL(k.tahsil_edilen)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap font-semibold">{formatTL(k.kalan)}</td>
                              <td className="px-2 py-1.5">
                                <Badge className={`${DURUM_BADGE[k.durum]} text-[10px]`}>
                                  {k.durum === "Gerçekleşti" && <CheckCircle2 className="mr-0.5 h-3 w-3" />}
                                  {k.durum}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* GECİKEN bölümü */}
      <div className="rounded-xl border border-red-300 bg-card overflow-hidden" data-testid="vade-geciken-bolumu">
        <button
          onClick={() => setGecikenAcik(!gecikenAcik)}
          className="flex w-full items-center gap-2 bg-red-50/70 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
          data-testid="vade-geciken-toggle"
        >
          <AlertTriangle className="h-4 w-4 text-red-700" />
          <div>
            <p className="text-sm font-semibold text-red-800">Geciken Tahsilatlar — Vadesi Geçmiş &amp; Tahsil Edilmemiş</p>
            <p className="text-xs text-red-700/70">{veri.geciken.adet.toLocaleString("tr-TR")} kayıt • toplam {formatTL(veri.geciken.toplam)} (tüm zamanlar)</p>
          </div>
          {gecikenAcik ? <ChevronDown className="ml-auto h-4 w-4 text-red-700" /> : <ChevronRight className="ml-auto h-4 w-4 text-red-700" />}
        </button>
        {gecikenAcik && (
          <>
            <div className="flex flex-wrap items-center gap-2 border-t border-red-200/60 bg-red-50/30 px-4 py-2">
              <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                <UserRound className="h-3.5 w-3.5" /> Sorumlu
              </Label>
              <Select value={sorumluFiltre} onValueChange={setSorumluFiltre}>
                <SelectTrigger className="h-9 w-56" data-testid="geciken-sorumlu-filtre">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="tumu">Tümü</SelectItem>
                  <SelectItem value="__bos">Sorumlu atanmamış</SelectItem>
                  {sorumlular.map((s) => (
                    <SelectItem key={s} value={s}>{s} — takip ettikleri</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[11px] text-muted-foreground">{gecikenListe.length.toLocaleString("tr-TR")} kayıt gösteriliyor • kalem satırındaki kalem simgesiyle sorumlu/arama/not girin</span>
            </div>
          <div className="max-h-[420px] overflow-y-auto overflow-x-auto">
            {gecikenListe.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground" data-testid="vade-geciken-bos">Geciken tahsilat yok. 🎉</div>
            ) : (
              <table className="w-full text-xs" data-testid="vade-geciken-table">
                <thead className="sticky top-0 bg-muted">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2">Vade</th>
                    <th className="px-3 py-2 text-right">Gecikme</th>
                    <th className="px-3 py-2">Belge / İş Emri</th>
                    <th className="px-3 py-2">Sigorta / Müşteri</th>
                    <th className="px-3 py-2 text-right">Kalan Tutar</th>
                    <th className="px-3 py-2">Durum</th>
                    <th className="px-3 py-2">Sorumlu</th>
                    <th className="px-3 py-2">Son Arama</th>
                    <th className="px-3 py-2">Not</th>
                    <th className="px-3 py-2 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {gecikenListe.map((k) => (
                    <tr key={k.id} className="border-t border-border/50" data-testid="vade-geciken-kayit">
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{formatDate(k.vade_tarihi)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-red-700 whitespace-nowrap">{k.gecikme_gun != null ? `${k.gecikme_gun} gün` : "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{k.belge_no || k.is_emri_no || "—"}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={k.sigorta_sirketi || k.musteri}>{k.sigorta_sirketi || k.musteri || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700 whitespace-nowrap">{formatTL(k.kalan)}</td>
                      <td className="px-3 py-2">
                        <Badge className={`${DURUM_BADGE[k.durum]} text-[10px]`}>{k.durum}</Badge>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap" data-testid="geciken-sorumlu-hucre">
                        {k.takip_sorumlu || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums" data-testid="geciken-son-arama-hucre">
                        {k.takip_son_arama ? formatDate(k.takip_son_arama) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 max-w-[160px] truncate" title={k.takip_not} data-testid="geciken-not-hucre">
                        {k.takip_not || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setTakipKayit(k)}
                          title="Takip bilgisi gir (sorumlu / son arama / not)"
                          data-testid="geciken-takip-duzenle-btn"
                        >
                          <PencilLine className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </>
        )}
      </div>

      <TakipBilgiDialog
        kayit={takipKayit}
        onClose={() => setTakipKayit(null)}
        onSaved={() => {
          setTakipKayit(null);
          fetchVeri();
        }}
      />
    </div>
  );
}

/* ================= GÜNLÜK AKIŞ (gün gün giriş / çıkış) ================= */

function GunlukAkis() {
  const { user } = useAuth();
  const yetkili = user.role === "yonetici" || user.role === "operasyon";
  const simdi = new Date();
  const [yil, setYil] = useState(String(simdi.getFullYear()));
  const [ay, setAy] = useState(String(simdi.getMonth() + 1));
  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detayGun, setDetayGun] = useState(null);
  const [ayarOpen, setAyarOpen] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [hesap, setHesap] = useState("tumu");

  // Günlük akış tablosunu tek tuşla Excel olarak indirir
  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      const res = await api.get("/nakit-akis/gunluk/excel", {
        params: { yil: parseInt(yil), ay: parseInt(ay) },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `3A-Gunluk-Nakit-Akisi-${yil}-${String(ay).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi.");
    } catch (err) {
      toast.error(getErrMsg(err, "Excel oluşturulamadı."));
    } finally {
      setExcelLoading(false);
    }
  };

  const fetchVeri = useCallback(async () => {
    setLoading(true);
    try {
      const params = { yil: parseInt(yil), ay: parseInt(ay) };
      if (hesap !== "tumu") params.hesap = hesap;
      const res = await api.get("/nakit-akis/gunluk", { params });
      setVeri(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Günlük akış yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, ay, hesap]);

  useEffect(() => {
    fetchVeri();
  }, [fetchVeri]);

  if (loading && !veri) {
    return (
      <div className="space-y-3" data-testid="gunluk-akis-loading">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (!veri) return null;

  const gunAdi = (t) => {
    try { return GUN_ADLARI[new Date(t + "T12:00:00").getDay()]; } catch { return ""; }
  };
  const haftaSonuMu = (t) => {
    try { const g = new Date(t + "T12:00:00").getDay(); return g === 0 || g === 6; } catch { return false; }
  };
  const t = veri.toplam;

  return (
    <div className="space-y-4" data-testid="gunluk-akis">
      {/* Dönem seçimi + Ödeme Günleri ayarı */}
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <CalendarDays className="h-3.5 w-3.5" /> Dönem
        </Label>
        <Select value={ay} onValueChange={setAy}>
          <SelectTrigger className="h-10 w-36" data-testid="gunluk-ay-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {AYLAR_TAM.map((a, i) => (
              <SelectItem key={a} value={String(i + 1)}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={yil} onValueChange={setYil}>
          <SelectTrigger className="h-10 w-28" data-testid="gunluk-yil-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {["2025", "2026", "2027"].map((y) => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <HesapFiltre value={hesap} onChange={setHesap} testid="gunluk-hesap-filtre" />
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            className="h-10 gap-1.5"
            onClick={handleExcel}
            disabled={excelLoading || loading}
            data-testid="gunluk-excel-btn"
          >
            {excelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-700" />}
            Excel İndir
          </Button>
          {yetkili && (
            <Button
              variant="outline"
              className="h-10 gap-1.5"
              onClick={() => setAyarOpen(true)}
              data-testid="gunluk-odeme-gunleri-btn"
            >
              <Settings2 className="h-4 w-4" />
              Ödeme Günleri
            </Button>
          )}
        </div>
      </div>

      {/* Ay özeti — 3 sade kart */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="gunluk-ozet-kartlari">
        <div className="rounded-xl border bg-card p-4" data-testid="gunluk-ozet-giris">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <ArrowUpCircle className="h-3.5 w-3.5 text-emerald-600" /> Toplam Giriş
          </p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-emerald-700">{formatTL(t.giris)}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Beklenen {formatTL(t.beklenen_tahsilat)} • Gerçekleşen {formatTL(t.gerceklesen_tahsilat)}
          </p>
          <p className="mt-1 flex items-center gap-2 text-[11px]" data-testid="gunluk-giris-kasa-banka">
            <span className="inline-flex items-center gap-1 text-amber-700"><Wallet className="h-3 w-3" /> Kasa {formatTL(t.kasa_giris)}</span>
            <span className="inline-flex items-center gap-1 text-sky-700"><Landmark className="h-3 w-3" /> Banka {formatTL(t.banka_giris)}</span>
          </p>
        </div>
        <div className="rounded-xl border bg-card p-4" data-testid="gunluk-ozet-cikis">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <ArrowDownCircle className="h-3.5 w-3.5 text-red-600" /> Toplam Çıkış
          </p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-red-700">{formatTL(t.cikis)}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Planlanan {formatTL(t.planlanan_odeme)} • Gerçekleşen {formatTL(t.gerceklesen_gider)}
          </p>
          <p className="mt-1 flex items-center gap-2 text-[11px]" data-testid="gunluk-cikis-kasa-banka">
            <span className="inline-flex items-center gap-1 text-amber-700"><Wallet className="h-3 w-3" /> Kasa {formatTL(t.kasa_cikis)}</span>
            <span className="inline-flex items-center gap-1 text-sky-700"><Landmark className="h-3 w-3" /> Banka {formatTL(t.banka_cikis)}</span>
          </p>
        </div>
        <div className={`rounded-xl border p-4 ${t.net >= 0 ? "border-emerald-200 bg-emerald-50/40" : "border-red-300 bg-red-50/50"}`} data-testid="gunluk-ozet-net">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Ay Net Akış</p>
          <p className={`mt-1 font-display text-xl font-bold tabular-nums ${t.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {formatTL(t.net)}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Giriş − Çıkış</p>
        </div>
      </div>

      {/* Gün gün liste */}
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="gunluk-gun-listesi">
        <div className="border-b bg-muted/40 px-4 py-3">
          <p className="text-sm font-semibold">{AYLAR_TAM[parseInt(ay) - 1]} {yil} — Gün Gün Nakit Akışı</p>
          <p className="text-xs text-muted-foreground">
            Giriş = beklenen tahsilat (vade) + gerçekleşen tahsilat • Çıkış = planlanan ödeme + gerçekleşen gider • satıra tıklayınca detay açılır
          </p>
        </div>
        <div className="grid grid-cols-[minmax(140px,1.3fr)_repeat(4,minmax(90px,1fr))] gap-2 border-b bg-muted/20 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span>Gün</span>
          <span className="text-right">Giriş</span>
          <span className="text-right">Çıkış</span>
          <span className="text-right">Net</span>
          <span className="text-right">Kümülatif</span>
        </div>
        <div className="divide-y divide-border/60">
          {veri.gunler.map((g) => {
            const aktif = g.giris > 0 || g.cikis > 0;
            const ozet = [
              g.beklenen_tahsilat > 0 && "beklenen tahsilat",
              g.gerceklesen_tahsilat > 0 && "tahsilat",
              g.planlanan_odeme > 0 && "planlı ödeme",
              g.gerceklesen_gider > 0 && "gider",
            ].filter(Boolean).join(" • ");
            return (
              <button
                key={g.tarih}
                onClick={() => aktif && setDetayGun(g)}
                disabled={!aktif}
                className={`grid w-full grid-cols-[minmax(140px,1.3fr)_repeat(4,minmax(90px,1fr))] items-center gap-2 px-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] ${
                  aktif ? "py-2.5 cursor-pointer hover:bg-muted/40" : "py-1.5 opacity-45"
                } ${g.bugun_mu ? "bg-[hsl(var(--accent))]/5" : haftaSonuMu(g.tarih) ? "bg-muted/20" : ""}`}
                data-testid={`gunluk-satir-${g.tarih}`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold tabular-nums ${
                    g.bugun_mu ? "bg-[hsl(var(--primary))] text-white" : "bg-muted text-foreground/70"
                  }`}>
                    {g.gun}
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold whitespace-nowrap">
                      {gunAdi(g.tarih)}
                      {g.bugun_mu && (
                        <Badge className="ml-1.5 bg-[hsl(var(--accent))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--accent))]/30 text-[9px] hover:bg-[hsl(var(--accent))]/15">
                          BUGÜN
                        </Badge>
                      )}
                    </div>
                    {aktif && <p className="truncate text-[10px] text-muted-foreground">{ozet}</p>}
                  </div>
                </div>
                <p className={`text-right text-sm font-bold tabular-nums whitespace-nowrap ${g.giris > 0 ? "text-emerald-700" : "text-muted-foreground/40"}`}>
                  {g.giris > 0 ? `+${formatTL(g.giris)}` : "—"}
                </p>
                <p className={`text-right text-sm font-bold tabular-nums whitespace-nowrap ${g.cikis > 0 ? "text-red-700" : "text-muted-foreground/40"}`}>
                  {g.cikis > 0 ? `−${formatTL(g.cikis)}` : "—"}
                </p>
                <p className={`text-right text-sm font-semibold tabular-nums whitespace-nowrap ${
                  g.net > 0 ? "text-emerald-700" : g.net < 0 ? "text-red-700" : "text-muted-foreground/40"
                }`}>
                  {g.net !== 0 ? formatTL(g.net) : "—"}
                </p>
                <p className={`text-right text-sm font-bold tabular-nums whitespace-nowrap ${g.kumulatif < 0 ? "text-red-700" : "text-foreground/80"}`}>
                  {formatTL(g.kumulatif)}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <DetayDialog
        acikVeri={detayGun && {
          baslik: `${formatDate(detayGun.tarih)} — Gün Detayı`,
          aciklama: `Giriş ${formatTL(detayGun.giris)} • Çıkış ${formatTL(detayGun.cikis)} • Net ${formatTL(detayGun.net)}`,
          detay: detayGun.detay,
        }}
        onClose={() => setDetayGun(null)}
      />
      <OdemeGunleriDialog open={ayarOpen} onOpenChange={setAyarOpen} onKaydedildi={fetchVeri} />

      {/* Ödemeler: bütçeden bağımsız nakit çıkışları (vergi, tedarikçi, kira vb.) */}
      {yetkili && <Odemeler yil={parseInt(yil)} ay={parseInt(ay)} onChanged={fetchVeri} />}
    </div>
  );
}

/* ---------- Gün detay dialogu ---------- */
const DETAY_BOLUMLERI = [
  { key: "beklenen", baslik: "Beklenen Tahsilatlar (Vade)", renk: "text-amber-700", icon: Clock },
  { key: "tahsilatlar", baslik: "Gerçekleşen Tahsilatlar", renk: "text-emerald-700", icon: ArrowUpCircle },
  { key: "planlanan", baslik: "Planlanan Ödemeler", renk: "text-blue-700", icon: CalendarDays },
  { key: "giderler", baslik: "Gerçekleşen Giderler", renk: "text-red-700", icon: ArrowDownCircle },
];

function DetayDialog({ acikVeri, onClose }) {
  const detay = acikVeri?.detay || {};
  const cikisKirilim = {};
  ["planlanan", "giderler"].forEach((k) => {
    (detay[k] || []).forEach((x) => {
      const kat = x.kategori || "Diğer";
      cikisKirilim[kat] = (cikisKirilim[kat] || 0) + (x.tutar || 0);
    });
  });
  const kirilimList = Object.entries(cikisKirilim).sort((a, b) => b[1] - a[1]);
  return (
    <Dialog open={!!acikVeri} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="gunluk-detay-dialog">
        {acikVeri && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">{acikVeri.baslik}</DialogTitle>
              <DialogDescription>{acikVeri.aciklama}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {kirilimList.length > 0 && (
                <div data-testid="nakit-detay-kategori-kirilim">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Çıkış — Kategori Kırılımı</p>
                  <div className="divide-y divide-border/50 rounded-lg border">
                    {kirilimList.map(([kat, tutar]) => (
                      <div key={kat} className="flex items-center gap-2 px-3 py-1.5 text-xs" data-testid="nakit-kategori-kirilim-satiri">
                        <span className="min-w-0 flex-1 truncate font-medium">{kat}</span>
                        <span className="shrink-0 font-semibold tabular-nums text-red-700">{formatTL(tutar)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {DETAY_BOLUMLERI.map((b) => {
                const liste = detay[b.key] || [];
                if (liste.length === 0) return null;
                const Icon = b.icon;
                const bolumToplam = liste.reduce((a, x) => a + (x.tutar || 0), 0);
                return (
                  <div key={b.key} data-testid={`gunluk-detay-${b.key}`}>
                    <p className={`mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${b.renk}`}>
                      <Icon className="h-3.5 w-3.5" /> {b.baslik}
                      <span className="ml-auto tabular-nums">{formatTL(bolumToplam)}</span>
                    </p>
                    <div className="divide-y divide-border/50 rounded-lg border">
                      {liste.map((x, i) => (
                        <div key={`${x.ad}-${x.no || ""}-${x.tutar}-${i}`} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid="gunluk-detay-satiri">
                          <span className="min-w-0 flex-1 truncate font-medium">{x.ad}</span>
                          {x.kategori && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{x.kategori}</span>}
                          {x.no && <span className="shrink-0 text-muted-foreground">{x.no}</span>}
                          <span className="shrink-0 font-semibold tabular-nums">{formatTL(x.tutar)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Ödeme Günleri ayar dialogu ---------- */
function OdemeGunleriDialog({ open, onOpenChange, onKaydedildi }) {
  const [kalemler, setKalemler] = useState([]);
  const [gunler, setGunler] = useState({});
  const [loading, setLoading] = useState(false);
  const [kaydediyor, setKaydediyor] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/butce-kalemleri")
      .then((res) => {
        const ks = res.data || [];
        setKalemler(ks);
        const g = {};
        ks.forEach((k) => {
          g[k.id] = k.odeme_gunu != null ? String(k.odeme_gunu) : "";
        });
        setGunler(g);
      })
      .catch(() => toast.error("Bütçe kalemleri yüklenemedi."))
      .finally(() => setLoading(false));
  }, [open]);

  const kaydet = async () => {
    setKaydediyor(true);
    try {
      const items = kalemler.map((k) => {
        const v = parseInt(gunler[k.id]);
        return { id: k.id, odeme_gunu: v >= 1 && v <= 31 ? v : null };
      });
      const res = await api.put("/butce-kalemleri/odeme-gunleri", { items });
      toast.success(res.data.message || "Ödeme günleri kaydedildi.");
      onOpenChange(false);
      onKaydedildi?.();
    } catch (err) {
      toast.error(getErrMsg(err, "Ödeme günleri kaydedilemedi."));
    } finally {
      setKaydediyor(false);
    }
  };

  const gruplar = {};
  kalemler.forEach((k) => {
    const kat = k.ana_kategori || "Diğer";
    (gruplar[kat] = gruplar[kat] || []).push(k);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="odeme-gunleri-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Settings2 className="h-4 w-4" /> Ödeme Günleri
          </DialogTitle>
          <DialogDescription>
            Her gider kaleminin aylık ödeme gününü girin (1–31). Örn. maaş = 5, kira = 24. Boş bırakılan kalem günlük planda görünmez.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto rounded-lg border">
          {loading ? (
            <div className="space-y-2 p-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : (
            Object.entries(gruplar).map(([kat, ks]) => (
              <div key={kat}>
                <p className="sticky top-0 z-10 bg-muted px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{kat}</p>
                {ks.map((k) => (
                  <div key={k.id} className="flex items-center gap-3 border-t border-border/40 px-3 py-1.5" data-testid="odeme-gunu-satiri">
                    <span className="min-w-0 flex-1 truncate text-xs font-medium">{k.kalem_adi}</span>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">Her ayın</span>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        placeholder="—"
                        value={gunler[k.id] ?? ""}
                        onChange={(e) => setGunler((p) => ({ ...p, [k.id]: e.target.value }))}
                        className="h-9 w-16 text-center tabular-nums"
                        data-testid={`odeme-gunu-input-${k.id}`}
                      />
                      <span className="text-[10px] text-muted-foreground">. günü</span>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
        <Button onClick={kaydet} disabled={kaydediyor || loading} className="w-full min-h-[44px]" data-testid="odeme-gunleri-kaydet-btn">
          {kaydediyor && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Kaydet
        </Button>
      </DialogContent>
    </Dialog>
  );
}

/* ================= HAFTALIK AKIŞ (seçilen ayın haftalık kırılımı) ================= */

function HaftalikAkis() {
  const simdi = new Date();
  const [yil, setYil] = useState(String(simdi.getFullYear()));
  const [ay, setAy] = useState(String(simdi.getMonth() + 1));
  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excelLoading, setExcelLoading] = useState(false);
  const [hesap, setHesap] = useState("tumu");
  const [detayHafta, setDetayHafta] = useState(null);

  const fetchVeri = useCallback(async () => {
    setLoading(true);
    try {
      const params = { yil: parseInt(yil), ay: parseInt(ay) };
      if (hesap !== "tumu") params.hesap = hesap;
      const res = await api.get("/nakit-akis/haftalik", { params });
      setVeri(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Haftalık akış yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, ay, hesap]);

  useEffect(() => {
    fetchVeri();
  }, [fetchVeri]);

  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      const res = await api.get("/nakit-akis/haftalik/excel", {
        params: { yil: parseInt(yil), ay: parseInt(ay) },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `3A-Haftalik-Nakit-Akisi-${yil}-${String(ay).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi.");
    } catch (err) {
      toast.error(getErrMsg(err, "Excel oluşturulamadı."));
    } finally {
      setExcelLoading(false);
    }
  };

  if (loading && !veri) {
    return (
      <div className="space-y-3" data-testid="haftalik-akis-loading">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!veri) return null;

  const t = veri.toplam;
  const trTarih = (s) => formatDate(s);

  return (
    <div className="space-y-4" data-testid="haftalik-akis">
      {/* Dönem seçimi + Excel */}
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <CalendarRange className="h-3.5 w-3.5" /> Dönem
        </Label>
        <Select value={ay} onValueChange={setAy}>
          <SelectTrigger className="h-10 w-36" data-testid="haftalik-ay-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {AYLAR_TAM.map((a, i) => (
              <SelectItem key={a} value={String(i + 1)}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={yil} onValueChange={setYil}>
          <SelectTrigger className="h-10 w-28" data-testid="haftalik-yil-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {["2025", "2026", "2027"].map((y) => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <HesapFiltre value={hesap} onChange={setHesap} testid="haftalik-hesap-filtre" />
        <Button
          variant="outline"
          className="ml-auto h-10 gap-1.5"
          onClick={handleExcel}
          disabled={excelLoading || loading}
          data-testid="haftalik-excel-btn"
        >
          {excelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-700" />}
          Excel İndir
        </Button>
      </div>

      {/* Ay özeti */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="haftalik-ozet-kartlari">
        <div className="rounded-xl border bg-card p-4">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <ArrowUpCircle className="h-3.5 w-3.5 text-emerald-600" /> Toplam Giriş
          </p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-emerald-700">{formatTL(t.giris)}</p>
          <p className="mt-1 flex items-center gap-2 text-[11px]" data-testid="haftalik-giris-kasa-banka">
            <span className="inline-flex items-center gap-1 text-amber-700"><Wallet className="h-3 w-3" /> Kasa {formatTL(t.kasa_giris)}</span>
            <span className="inline-flex items-center gap-1 text-sky-700"><Landmark className="h-3 w-3" /> Banka {formatTL(t.banka_giris)}</span>
          </p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <ArrowDownCircle className="h-3.5 w-3.5 text-red-600" /> Toplam Çıkış
          </p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-red-700">{formatTL(t.cikis)}</p>
          <p className="mt-1 flex items-center gap-2 text-[11px]" data-testid="haftalik-cikis-kasa-banka">
            <span className="inline-flex items-center gap-1 text-amber-700"><Wallet className="h-3 w-3" /> Kasa {formatTL(t.kasa_cikis)}</span>
            <span className="inline-flex items-center gap-1 text-sky-700"><Landmark className="h-3 w-3" /> Banka {formatTL(t.banka_cikis)}</span>
          </p>
        </div>
        <div className={`rounded-xl border p-4 ${t.net >= 0 ? "border-emerald-200 bg-emerald-50/40" : "border-red-300 bg-red-50/50"}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Ay Net Akış</p>
          <p className={`mt-1 font-display text-xl font-bold tabular-nums ${t.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatTL(t.net)}</p>
        </div>
      </div>

      {/* Hafta hafta tablo */}
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="haftalik-tablo">
        <div className="border-b bg-muted/40 px-4 py-3">
          <p className="text-sm font-semibold">{AYLAR_TAM[parseInt(ay) - 1]} {yil} — Haftalık Nakit Akışı</p>
          <p className="text-xs text-muted-foreground">
            Haftalar Pazartesi başlangıçlıdır • Giriş = beklenen tahsilat (vade) + gerçekleşen tahsilat • Çıkış = planlanan ödeme + gerçekleşen gider
          </p>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/20 text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Hafta</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Dönem</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Beklenen Tahsilat</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Gerçekleşen Tahsilat</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Toplam Giriş</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Planlanan Ödeme</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Gerçekleşen Gider</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Toplam Çıkış</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Net</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Kümülatif</th>
              </tr>
            </thead>
            <tbody>
              {veri.haftalar.map((h) => (
                <tr
                  key={h.hafta}
                  className="border-t border-border/50 cursor-pointer transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]"
                  onClick={() => setDetayHafta(h)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDetayHafta(h);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  title="Hafta detayını aç"
                  data-testid={`haftalik-satir-${h.hafta}`}
                >
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className="inline-flex h-7 min-w-[56px] items-center justify-center rounded-lg bg-muted px-2 text-xs font-bold">
                      {h.hafta}. Hafta
                    </span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-muted-foreground">
                    {trTarih(h.baslangic)} – {trTarih(h.bitis)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{h.beklenen_tahsilat > 0 ? formatTL(h.beklenen_tahsilat) : "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-emerald-700">{h.gerceklesen_tahsilat > 0 ? formatTL(h.gerceklesen_tahsilat) : "—"}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold ${h.giris > 0 ? "text-emerald-700" : "text-muted-foreground/50"}`}>
                    {h.giris > 0 ? `+${formatTL(h.giris)}` : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{h.planlanan_odeme > 0 ? formatTL(h.planlanan_odeme) : "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-red-700">{h.gerceklesen_gider > 0 ? formatTL(h.gerceklesen_gider) : "—"}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold ${h.cikis > 0 ? "text-red-700" : "text-muted-foreground/50"}`}>
                    {h.cikis > 0 ? `−${formatTL(h.cikis)}` : "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-bold ${h.net > 0 ? "text-emerald-700" : h.net < 0 ? "text-red-700" : "text-muted-foreground/50"}`}>
                    {h.net !== 0 ? formatTL(h.net) : "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-bold ${h.kumulatif < 0 ? "text-red-700" : ""}`}>
                    {formatTL(h.kumulatif)}
                  </td>
                </tr>
              ))}
              {/* TOPLAM satırı */}
              <tr className="border-t-2 border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))] text-white" data-testid="haftalik-toplam-satiri">
                <td className="px-3 py-2.5 font-bold" colSpan={2}>TOPLAM</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold whitespace-nowrap">{formatTL(t.beklenen_tahsilat)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold whitespace-nowrap">{formatTL(t.gerceklesen_tahsilat)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold whitespace-nowrap text-[hsl(var(--accent))]">{formatTL(t.giris)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold whitespace-nowrap">{formatTL(t.planlanan_odeme)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold whitespace-nowrap">{formatTL(t.gerceklesen_gider)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold whitespace-nowrap text-[hsl(var(--accent))]">{formatTL(t.cikis)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-bold whitespace-nowrap ${t.net < 0 ? "text-red-300" : "text-[hsl(var(--accent))]"}`}>{formatTL(t.net)}</td>
                <td className="px-3 py-2.5" />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <DetayDialog
        acikVeri={detayHafta && {
          baslik: `${detayHafta.hafta}. Hafta (${trTarih(detayHafta.baslangic)} – ${trTarih(detayHafta.bitis)}) — Detay`,
          aciklama: `Giriş ${formatTL(detayHafta.giris)} • Çıkış ${formatTL(detayHafta.cikis)} • Net ${formatTL(detayHafta.net)}`,
          detay: detayHafta.detay,
        }}
        onClose={() => setDetayHafta(null)}
      />
    </div>
  );
}

/* ================= AYLIK AKIŞ (12 ay yatay tablo) ================= */

function AylikAkis() {
  const simdi = new Date();
  const [yil, setYil] = useState(String(simdi.getFullYear()));
  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excelLoading, setExcelLoading] = useState(false);
  const [hesap, setHesap] = useState("tumu");
  const [detayAy, setDetayAy] = useState(null);

  const fetchVeri = useCallback(async () => {
    setLoading(true);
    try {
      const params = { yil: parseInt(yil) };
      if (hesap !== "tumu") params.hesap = hesap;
      const res = await api.get("/nakit-akis/aylik", { params });
      setVeri(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Aylık akış yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, hesap]);

  useEffect(() => {
    fetchVeri();
  }, [fetchVeri]);

  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      const res = await api.get("/nakit-akis/aylik/excel", {
        params: { yil: parseInt(yil) },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `3A-Aylik-Nakit-Akisi-${yil}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi.");
    } catch (err) {
      toast.error(getErrMsg(err, "Excel oluşturulamadı."));
    } finally {
      setExcelLoading(false);
    }
  };

  if (loading && !veri) {
    return (
      <div className="space-y-3" data-testid="aylik-akis-loading">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!veri) return null;

  const aylar = veri.aylar;
  const t = veri.toplam;

  return (
    <div className="space-y-4" data-testid="aylik-akis">
      {/* Yıl seçimi + Excel */}
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <Table2 className="h-3.5 w-3.5" /> Yıl
        </Label>
        <Select value={yil} onValueChange={setYil}>
          <SelectTrigger className="h-10 w-28" data-testid="aylik-yil-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {["2025", "2026", "2027"].map((y) => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <HesapFiltre value={hesap} onChange={setHesap} testid="aylik-hesap-filtre" />
        <span className="text-[11px] text-muted-foreground">Her ay bir sütun • ay adına tıklayınca detay açılır</span>
        <Button
          variant="outline"
          className="ml-auto h-10 gap-1.5"
          onClick={handleExcel}
          disabled={excelLoading || loading}
          data-testid="aylik-excel-btn"
        >
          {excelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-700" />}
          Excel İndir
        </Button>
      </div>

      {/* Yıl özeti */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="aylik-ozet-kartlari">
        <div className="rounded-xl border bg-card p-4">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <ArrowUpCircle className="h-3.5 w-3.5 text-emerald-600" /> Yıllık Toplam Giriş
          </p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-emerald-700">{formatTL(t.giris)}</p>
          <p className="mt-1 flex items-center gap-2 text-[11px]" data-testid="aylik-giris-kasa-banka">
            <span className="inline-flex items-center gap-1 text-amber-700"><Wallet className="h-3 w-3" /> Kasa {formatTL(t.kasa_giris)}</span>
            <span className="inline-flex items-center gap-1 text-sky-700"><Landmark className="h-3 w-3" /> Banka {formatTL(t.banka_giris)}</span>
          </p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <ArrowDownCircle className="h-3.5 w-3.5 text-red-600" /> Yıllık Toplam Çıkış
          </p>
          <p className="mt-1 font-display text-xl font-bold tabular-nums text-red-700">{formatTL(t.cikis)}</p>
          <p className="mt-1 flex items-center gap-2 text-[11px]" data-testid="aylik-cikis-kasa-banka">
            <span className="inline-flex items-center gap-1 text-amber-700"><Wallet className="h-3 w-3" /> Kasa {formatTL(t.kasa_cikis)}</span>
            <span className="inline-flex items-center gap-1 text-sky-700"><Landmark className="h-3 w-3" /> Banka {formatTL(t.banka_cikis)}</span>
          </p>
        </div>
        <div className={`rounded-xl border p-4 ${t.net >= 0 ? "border-emerald-200 bg-emerald-50/40" : "border-red-300 bg-red-50/50"}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Yıl Net Akış</p>
          <p className={`mt-1 font-display text-xl font-bold tabular-nums ${t.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatTL(t.net)}</p>
        </div>
      </div>

      {/* 12 ay yatay tablo — mevcut AkisTablosu bileşeni yeniden kullanılır */}
      <AkisTablosu
        testid="aylik-akis-tablosu"
        baslik={`${yil} — Aylık Nakit Akışı (12 Ay Yatay)`}
        altBaslik="Giriş = beklenen tahsilat (vade) + gerçekleşen tahsilat • Çıkış = planlanan ödeme + gerçekleşen gider • ay adına tıklayınca detay"
        onAyClick={(i) => setDetayAy(aylar[i])}
        satirlar={[
          { etiket: "Beklenen Tahsilat", degerler: aylar.map((a) => a.beklenen_tahsilat), tip: "duz" },
          { etiket: "Gerçekleşen Tahsilat", degerler: aylar.map((a) => a.gerceklesen_tahsilat), tip: "duz" },
          { etiket: "Toplam Giriş", degerler: aylar.map((a) => a.giris), tip: "duz" },
          { etiket: "Planlanan Ödeme (−)", degerler: aylar.map((a) => a.planlanan_odeme), tip: "eksi" },
          { etiket: "Gerçekleşen Gider (−)", degerler: aylar.map((a) => a.gerceklesen_gider), tip: "eksi" },
          { etiket: "Toplam Çıkış (−)", degerler: aylar.map((a) => a.cikis), tip: "eksi" },
          { etiket: "NET AKIŞ", degerler: aylar.map((a) => a.net), tip: "net" },
          { etiket: "KÜMÜLATİF", degerler: aylar.map((a) => a.kumulatif), tip: "kumulatif" },
        ]}
      />

      <DetayDialog
        acikVeri={detayAy && {
          baslik: `${AYLAR_TAM[detayAy.ay - 1]} ${yil} — Ay Detayı`,
          aciklama: `Giriş ${formatTL(detayAy.giris)} • Çıkış ${formatTL(detayAy.cikis)} • Net ${formatTL(detayAy.net)}`,
          detay: detayAy.detay,
        }}
        onClose={() => setDetayAy(null)}
      />
    </div>
  );
}


/* ================= KASA / BANKA FİLTRESİ ================= */

function HesapFiltre({ value, onChange, testid }) {
  return (
    <div
      className="flex items-center gap-0.5 rounded-lg border bg-card p-0.5"
      title="Kasa/Banka filtresi gerçekleşen tahsilat ve giderleri filtreler; beklenen/planlanan kalemler her görünümde kalır"
      data-testid={testid}
    >
      {[["tumu", "Tümü"], ["kasa", "Kasa"], ["banka", "Banka"]].map(([v, l]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
            value === v ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-muted"
          }`}
          data-testid={`${testid}-${v}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/* ================= VADE TARİHİ EKSİK KAYIT UYARISI ================= */

function VadesizUyari() {
  const { user } = useAuth();
  const yetkili = user.role === "yonetici" || user.role === "operasyon";
  const [veri, setVeri] = useState(null);
  const [open, setOpen] = useState(false);
  const [secili, setSecili] = useState({});
  const [topluTarih, setTopluTarih] = useState("");
  const [tekliTarih, setTekliTarih] = useState({});
  const [kaydediyor, setKaydediyor] = useState(false);

  const fetchVeri = useCallback(async () => {
    try {
      const res = await api.get("/nakit-akis/vadesiz");
      setVeri(res.data);
    } catch {
      /* kart sessizce gizlenir */
    }
  }, []);

  useEffect(() => {
    fetchVeri();
  }, [fetchVeri]);

  if (!veri || veri.adet === 0) return null;

  const seciliIds = Object.keys(secili).filter((id) => secili[id]);

  const tekliKaydet = async (id) => {
    const tarih = tekliTarih[id];
    if (!tarih) return toast.error("Önce vade tarihi seçin.");
    setKaydediyor(true);
    try {
      await api.patch(`/ciro/${id}/vade`, { vade_tarihi: tarih });
      toast.success("Vade tarihi kaydedildi.");
      fetchVeri();
    } catch (err) {
      toast.error(getErrMsg(err, "Vade tarihi kaydedilemedi."));
    } finally {
      setKaydediyor(false);
    }
  };

  const topluKaydet = async () => {
    if (!topluTarih) return toast.error("Toplu vade tarihi seçin.");
    if (seciliIds.length === 0) return toast.error("En az bir kayıt seçin.");
    setKaydediyor(true);
    try {
      const res = await api.post("/ciro/toplu-vade", { ids: seciliIds, vade_tarihi: topluTarih });
      toast.success(res.data.message);
      setSecili({});
      fetchVeri();
    } catch (err) {
      toast.error(getErrMsg(err, "Toplu vade güncellenemedi."));
    } finally {
      setKaydediyor(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50/70 px-4 py-3 text-left transition-colors hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        data-testid="vadesiz-uyari-karti"
      >
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            Vade Tarihi Girilmemiş: {veri.adet.toLocaleString("tr-TR")} kayıt, {formatTL(veri.toplam)}
          </p>
          <p className="text-[11px] text-amber-800/80">Bu kayıtlar nakit akış planına giremiyor — tıklayıp vade tarihlerini girin.</p>
        </div>
        <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-amber-600" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="vadesiz-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Vade Tarihi Girilmemiş Kayıtlar</DialogTitle>
            <DialogDescription>
              {veri.adet.toLocaleString("tr-TR")} açık kayıt • toplam {formatTL(veri.toplam)} — tekli girin veya seçtiklerinize toplu uygulayın.
            </DialogDescription>
          </DialogHeader>
          {yetkili && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
              <Label className="text-xs">Toplu vade:</Label>
              <Input
                type="date"
                value={topluTarih}
                onChange={(e) => setTopluTarih(e.target.value)}
                className="h-9 w-40"
                data-testid="vadesiz-toplu-tarih"
              />
              <Button size="sm" className="h-9" onClick={topluKaydet} disabled={kaydediyor} data-testid="vadesiz-toplu-uygula-btn">
                Seçilenlere Uygula ({seciliIds.length})
              </Button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-2 w-8" />
                  <th className="px-2 py-2">Tarih</th>
                  <th className="px-2 py-2">Belge / İş Emri</th>
                  <th className="px-2 py-2">Müşteri</th>
                  <th className="px-2 py-2 text-right">Kalan</th>
                  <th className="px-2 py-2">Vade Tarihi</th>
                </tr>
              </thead>
              <tbody>
                {veri.kayitlar.map((k) => (
                  <tr key={k.id} className="border-t border-border/50" data-testid="vadesiz-kayit-satiri">
                    <td className="px-2 py-1.5">
                      <Checkbox
                        checked={!!secili[k.id]}
                        onCheckedChange={(c) => setSecili((p) => ({ ...p, [k.id]: !!c }))}
                        data-testid={`vadesiz-secim-${k.id}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{formatDate(k.tarih)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{k.belge_no || k.is_emri_no || "—"}</td>
                    <td className="px-2 py-1.5 max-w-[200px] truncate" title={k.sigorta_sirketi || k.musteri_aciklama}>
                      {k.sigorta_sirketi || k.musteri_aciklama || "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold whitespace-nowrap">{formatTL(k.kalan)}</td>
                    <td className="px-2 py-1.5">
                      {yetkili ? (
                        <div className="flex items-center gap-1.5">
                          <Input
                            type="date"
                            value={tekliTarih[k.id] || ""}
                            onChange={(e) => setTekliTarih((p) => ({ ...p, [k.id]: e.target.value }))}
                            className="h-8 w-36"
                            data-testid={`vadesiz-tarih-${k.id}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-2"
                            onClick={() => tekliKaydet(k.id)}
                            disabled={kaydediyor}
                            title="Vade tarihini kaydet"
                            data-testid={`vadesiz-kaydet-${k.id}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ================= GECİKEN TAHSİLAT TAKİP BİLGİSİ DİALOGU ================= */

function TakipBilgiDialog({ kayit, onClose, onSaved }) {
  const [personeller, setPersoneller] = useState([]);
  const [sorumlu, setSorumlu] = useState("");
  const [sonArama, setSonArama] = useState("");
  const [takipNot, setTakipNot] = useState("");
  const [kaydediyor, setKaydediyor] = useState(false);

  useEffect(() => {
    if (!kayit) return;
    setSorumlu(kayit.takip_sorumlu || "");
    setSonArama(kayit.takip_son_arama || "");
    setTakipNot(kayit.takip_not || "");
    api
      .get("/personeller")
      .then((res) => setPersoneller(res.data || []))
      .catch(() => setPersoneller([]));
  }, [kayit]);

  const kaydet = async () => {
    setKaydediyor(true);
    try {
      await api.patch(`/ciro/${kayit.id}/takip-bilgi`, {
        takip_sorumlu: sorumlu,
        takip_son_arama: sonArama,
        takip_not: takipNot,
      });
      toast.success("Takip bilgisi kaydedildi (loglandı).");
      onSaved?.();
    } catch (err) {
      toast.error(getErrMsg(err, "Takip bilgisi kaydedilemedi."));
    } finally {
      setKaydediyor(false);
    }
  };

  return (
    <Dialog open={!!kayit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="takip-bilgi-dialog">
        {kayit && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Takip Bilgisi — {kayit.belge_no || kayit.is_emri_no || "Kayıt"}</DialogTitle>
              <DialogDescription>
                {kayit.sigorta_sirketi || kayit.musteri || ""} • kalan {formatTL(kayit.kalan)} • her değişiklik loglanır
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <UserRound className="h-3.5 w-3.5" /> Sorumlu Kişi
                </Label>
                {personeller.length > 0 ? (
                  <Select value={sorumlu || "__yok"} onValueChange={(v) => setSorumlu(v === "__yok" ? "" : v)}>
                    <SelectTrigger className="h-10" data-testid="takip-sorumlu-select">
                      <SelectValue placeholder="Personel seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                      <SelectItem value="__yok">— Sorumlu yok —</SelectItem>
                      {personeller.map((p) => (
                        <SelectItem key={p.id} value={p.ad_soyad}>{p.ad_soyad}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={sorumlu}
                    onChange={(e) => setSorumlu(e.target.value)}
                    placeholder="Sorumlu kişi adı"
                    className="h-10"
                    data-testid="takip-sorumlu-input"
                  />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Son Aranma Tarihi</Label>
                <Input
                  type="date"
                  value={sonArama}
                  onChange={(e) => setSonArama(e.target.value)}
                  className="h-10"
                  data-testid="takip-son-arama-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Not</Label>
                <Textarea
                  value={takipNot}
                  onChange={(e) => setTakipNot(e.target.value)}
                  placeholder="örn. Muhasebe yetkilisiyle görüşüldü, ay sonunda ödeme sözü verildi"
                  rows={3}
                  data-testid="takip-not-input"
                />
              </div>
              <Button onClick={kaydet} disabled={kaydediyor} className="w-full min-h-[44px]" data-testid="takip-bilgi-kaydet-btn">
                {kaydediyor && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Kaydet
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
